import React from 'react';
import { Link } from 'react-router-dom';
import { Layers, ListOrdered } from 'lucide-react';

/**
 * Small, additive callout linking to the League Table & Rafts methodology.
 *
 * Drop this into any existing service page WITHOUT rewriting the page's
 * hero, H1 or main content — the purpose is to add topical depth and an
 * internal link into the /methodology hub, not to disturb pages that are
 * already ranking.
 *
 * `context` prop lets each host page tailor the copy so the callout reads
 * naturally in context (e.g. "for domestic MEES" on the domestic-advice
 * page, "for commercial MEES" on the mees-commercial-buildings page).
 */
const MethodologyCallout = ({
  context = 'For every advice report',
  reportCode = 'DEAR / NDEAR',
  className = '',
}) => (
  <section className={`py-14 bg-white ${className}`}>
    <div className="container mx-auto px-4">
      <div className="max-w-4xl mx-auto">
        <div className="p-6 md:p-8 rounded-2xl border-2 border-green-200 bg-gradient-to-br from-green-50 via-white to-green-50">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex md:flex-col gap-3 md:gap-2 flex-shrink-0">
              <ListOrdered className="w-8 h-8 text-green-600" />
              <Layers className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-2">Our methodology</div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">The League Table &amp; Rafts framework</h3>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                {context} we apply a systematic two-stage process: each candidate improvement measure is first modelled in isolation and ranked across five criteria (EPC points, energy saving, CO<sub>2</sub>, cost saving, payback), then the top-ranked measures are grouped into three cumulative implementation Rafts — priority, Band C target, and deep retrofit. This is how the <strong>{reportCode}</strong> reports we produce turn a single EPC into three costed, modelled pathways.
              </p>
              <Link
                to="/methodology"
                data-testid="methodology-callout-link"
                className="inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:text-green-800 hover:underline"
              >
                Read the full methodology <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default MethodologyCallout;
